import { z } from 'zod';

/**
 * Where a staff-recorded payment stands (`03-payments-iap.md` §Data flow —
 * Direct payments).
 *
 * `draft → awaiting_confirmation → confirmed | rejected`, and
 * `confirmed → refund_recorded`. Every one of those arrows is **irreversible and
 * audited**, which is why they are transitions on a closed enum rather than a
 * mutable status field: a value the platform has no rules for would be an order
 * nobody can decide.
 *
 * - `draft` — entered but not yet put to a decision. The one status that owns
 *   nothing: no grant is claimed and no entitlement exists.
 * - `awaiting_confirmation` — the terms are fixed, the member's discount grants
 *   are **claimed**, and the order is waiting for evidence of payment. A future
 *   local gateway (Tap/Moyasar) becomes a second creator of orders in exactly
 *   this state, which is why `method` is a column and not a branch.
 * - `confirmed` — the entitlement has been granted. Terminal.
 * - `rejected` — no entitlement, and the claimed grants are given **back**.
 *   Terminal.
 * - `refund_recorded` — the money was returned and the entitlement revoked. A
 *   manual, audited exception rather than an ordinary step, and it does **not**
 *   give the discount grants back (see {@link REFUND_KEEPS_GRANTS_SPENT}).
 */
export const DIRECT_PAYMENT_ORDER_STATUSES = [
  'draft',
  'awaiting_confirmation',
  'confirmed',
  'rejected',
  'refund_recorded',
] as const;

export const DirectPaymentOrderStatusSchema = z.enum(DIRECT_PAYMENT_ORDER_STATUSES);

export type DirectPaymentOrderStatus = (typeof DIRECT_PAYMENT_ORDER_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set<string>(DIRECT_PAYMENT_ORDER_STATUSES);

export function isDirectPaymentOrderStatus(value: string): value is DirectPaymentOrderStatus {
  return STATUS_SET.has(value);
}

/**
 * The statuses from which an order may still be confirmed or rejected — the
 * predicate those two claims re-check in their own `WHERE` (N27).
 *
 * `draft` is **not** in it. A draft has claimed nothing, so confirming one would
 * hand out an entitlement against a price whose discount was never reserved; it
 * is submitted first, which is the write that does the claiming.
 */
export const DECIDABLE_ORDER_STATUSES: readonly DirectPaymentOrderStatus[] = [
  'awaiting_confirmation',
];

/**
 * Why a refund leaves the member's discount grants spent.
 *
 * Stated here because it is a **policy**, not an oversight, and the next reader
 * will otherwise assume symmetry with rejection. Matrix row 15 names rejection
 * as the release trigger and nothing else. Giving the grants back on a refund
 * would make order → refund a loop that returns the discount every time, so a
 * member could farm one earned grant indefinitely. A refund is an exception a
 * staff member takes deliberately, and if the discount should be returned with
 * it, that is a fresh grant somebody decides to issue — visible, attributable,
 * and not a side effect.
 */
export const REFUND_KEEPS_GRANTS_SPENT = true;
